import React, { useState, useEffect } from 'react';

import { Modal } from 'antd';
import { useMedia } from 'react-media';

import { Form, WithLoading, EmailSelection } from 'common/components';

import DownloadFolderLayout from './DownloadFolderLayout';
import DownloadFolderStep from './DownloadFolderStep';
import DownloadFolderModalFooter from './DownloadFolderModalFooter';
import DownloadFolderSettings from './settings/DownloadFolderSettings';
import DownloadFolderSchedule from './schedule/DownloadFolderSchedule';

import {
  useFolderDownloadSettingQuery,
  useFolderDownloadScheduleQuery,
  useFolderDownloadProductMappingQuery,
} from 'hooks/folders/queries';
import {
  DOWNLOAD_FOLDER_DEFAULT_SETTING,
  useDownloadFolder,
  useConfigSchedule,
} from 'hooks/folders/useDownloadFolder';

import './DownloadFolder.less';

const DownloadFolderModal = (props) => {
  const [form] = Form.useForm();
  const deliveryMethod = Form.useWatch('deliveryMethod', form);
  const isSmallScreen = useMedia({ query: '(max-width: 1440px)' });

  const {
    folderId,
    visible,
    closeModal,
    initialStep = 0,
    schedulerData, //* update scheduler
    updateScheduler,
  } = props;

  const [settingStep, setSettingStep] = useState(initialStep);
  const [visibleEmailSelection, setVisibleEmailSelection] = useState(false);

  const {
    downloadViaEmails,
    downloadFolder,
    initDownloadSetting,
    initProductMapping,
  } = useDownloadFolder({ form, closeModal: () => onCancel });

  const { initScheduleDownload, scheduleDownload } = useConfigSchedule({
    form,
    folderId,
    closeModal: () => {
      onCancel();
    },
  });

  const {
    data: latestDownloadSetting,
    isLoading,
    isFetching,
  } = useFolderDownloadSettingQuery({
    folderId,
    enabled: !!folderId && visible,
  });

  const downloadSetting =
    schedulerData?.downloadSetting ||
    latestDownloadSetting?.folderDownloadSetting ||
    {};

  const mappingId = downloadSetting?.mappingId;

  const { data: latestScheduleDownload } = useFolderDownloadScheduleQuery({
    folderId,
    enabled:
      visible &&
      !!folderId &&
      settingStep === 1 && //* schedule step
      !schedulerData, //* prevent calling when updating scheduler,
  });

  const scheduleSetup =
    schedulerData?.scheduleSetup || latestScheduleDownload?.scheduleSetup;

  const { data: mappingData } = useFolderDownloadProductMappingQuery({
    mappingId,
    enabled: !!mappingId,
  });

  const isExecuteNowStep = settingStep === 0;
  const isScheduleStep = settingStep === 1;

  const initForm = () => {
    form.setFieldsValue({ folderId });
  };

  const executeDownloadNow = async () => {
    const formValues = await form.validateFields();

    if (!formValues) return;

    if (formValues?.deliveryMethod === 'email') {
      setVisibleEmailSelection(true);

      return;
    }

    //* deliveryMethod === 'direct'
    downloadFolder(formValues);
  };

  const onCancel = () => {
    form.resetFields();
    setSettingStep(initialStep);
    closeModal();
  };

  const onChangeStep = (current) => {
    setSettingStep(current);
  };

  const onSendMultiEmail = async (emails) => {
    setVisibleEmailSelection(false);
    downloadViaEmails(emails);
  };

  const onClear = () => {
    form.resetFields();
  };

  const onReset = () => {
    initDownloadSetting(downloadSetting);
    initScheduleDownload(scheduleSetup);
    initProductMapping(mappingData);
  };

  const onScheduleSetting = () => {
    setSettingStep(1);
  };

  const onBackToDownloadSetting = () => {
    setSettingStep(0);
  };

  const onSchedule = async () => {
    try {
      const formValues = await form.validateFields();

      if (!formValues) return;

      const test = new Date(formValues.runAt._d);
      const newDate = new Date(
        test.getTime() - test.getTimezoneOffset() * 60000
      );

      const params = {
        ...formValues,
        runAt: newDate.toISOString(),
        userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      //* update scheduler
      if (updateScheduler) {
        const updateResult = await updateScheduler(params);

        if (updateResult.isSuccess) onCancel(); //* close modal and reset state

        return;
      }

      //* set scheduler
      scheduleDownload(params);
    } catch (error) {
      console.log('error: ', error);
    }
  };

  const onFormChange = (changedValues, allValue) => {
    let updatedValues = {};
    if (changedValues.specificScheduleOrdinalNumeral) {
      updatedValues.specificScheduleOrdinalNumeral = [
        changedValues?.specificScheduleOrdinalNumeral?.[
          changedValues?.specificScheduleOrdinalNumeral.length - 1
        ],
      ].filter(Boolean);
      updatedValues.specificScheduleDaysOfWeek = [];
    }

    if (changedValues.specificScheduleDaysOfWeek) {
      if (allValue.specificScheduleOrdinalNumeral.length)
        updatedValues.specificScheduleDaysOfWeek = [
          changedValues?.specificScheduleDaysOfWeek?.[
            changedValues?.specificScheduleDaysOfWeek.length - 1
          ],
        ].filter(Boolean);
    }

    if (changedValues?.interval)
      updatedValues = {
        ...updatedValues,
        runEvery: 0,
        onDate: [],
        atDays: [],
        atMonth: [],
        specificScheduleDaysOfWeek: [],
        specificScheduleOrdinalNumeral: [],
      };

    if (changedValues?.atDays?.length > 0)
      updatedValues.specificScheduleDaysOfWeek = [];

    form.setFieldsValue(updatedValues);
  };

  useEffect(() => {
    if (visible) initForm(downloadSetting);
  }, [visible, folderId]);

  useEffect(() => {
    if (visible) initDownloadSetting(downloadSetting);
  }, [visible, JSON.stringify(downloadSetting)]);

  useEffect(() => {
    mappingData && visible && initProductMapping(mappingData);
  }, [visible, JSON.stringify(mappingData)]);

  useEffect(() => {
    if (visible) initScheduleDownload(scheduleSetup);
  }, [visible, JSON.stringify(scheduleSetup)]);

  useEffect(() => {
    setSettingStep(initialStep);
  }, [initialStep]);

  return (
    <Modal
      className='download-folder'
      title='Download Folder'
      visible={visible}
      width={900}
      destroyOnClose
      centered={isSmallScreen}
      maskClosable={false}
      bodyStyle={{ height: 'min(600px, 80vh)' }}
      footer={
        <DownloadFolderModalFooter
          deliveryMethod={deliveryMethod}
          settingStep={settingStep}
          onClear={onClear}
          onReset={onReset}
          onCancel={onCancel}
          onSchedule={onSchedule}
          onExecuteNow={executeDownloadNow}
          onScheduleSetting={onScheduleSetting}
          onBackToDownloadSetting={onBackToDownloadSetting}
        />
      }
      onCancel={onCancel}
    >
      <WithLoading loading={isLoading && isFetching}>
        <DownloadFolderLayout folderId={folderId}>
          <DownloadFolderStep
            slot='step'
            current={settingStep}
            deliveryMethod={deliveryMethod}
            onChange={onChangeStep}
          />

          <Form
            form={form}
            slot='settings'
            id='download-folder-form'
            className='scroller--y'
            initialValues={{ folderId, ...DOWNLOAD_FOLDER_DEFAULT_SETTING }}
            style={{ height: '100%' }}
            onValuesChange={onFormChange}
          >
            <Form.Item name='folderId' hidden noStyle />
            <DownloadFolderSettings visible={isExecuteNowStep} />
            {isScheduleStep ? <DownloadFolderSchedule /> : null}
          </Form>
        </DownloadFolderLayout>
      </WithLoading>
      <EmailSelection
        onClose={() => setVisibleEmailSelection(false)}
        visible={visibleEmailSelection}
        onSendEmail={onSendMultiEmail}
      />
    </Modal>
  );
};

export default DownloadFolderModal;
